import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import {
  BiAggregation,
  FieldConfigurationType,
  FormatType,
  MeasureDateRange,
} from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type {
  AggregationDescriptor,
  ConditionField,
  CustomColumnField,
  DimensionDescriptor,
  DimensionField,
  MeasureDescriptor,
  MetricDescriptor,
  MetricField,
  PivotConfiguration,
  PivotGeneralSettings,
  ReportField,
  SortField,
} from "../../../../../../../../api/types/svTypes.ts";
import {
  AreaItemType,
  DimensionFieldType,
  ItemDataType,
  MeasureUsage,
  MetaItemType,
} from "../../../../../../../../api/types/svTypes.ts";
import { valueToConfiguration } from "./configurations";

export const createPivotReportConfiguration = (
  conditions: ConditionField[],
  rows: ReportField[],
  columns: ReportField[],
  values: ReportField[],
  sorts: SortField[],
  settings: PivotGeneralSettings
) => {
  const reportConfiguration: PivotConfiguration = {
    conditions: conditions.map((c) => c.config),
    rows: rows.map((r) => r.config),
    columns: columns.map((c) => c.config),
    values: values.map(valueToConfiguration),
    sort: sorts.map((s) => s.config),
    settings: { ...settings },
  };
  return reportConfiguration;
};

export const configurationToRows = (
  configRows: FieldConfiguration[],
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[]
) => {
  try {
    const rows = configRows
      .map((config) => {
        if (config.type === FieldConfigurationType.Measure) {
          const measure = measures.find((d) => d.name === config.name);
          if (!measure || !measure.capabilities.isScalar) {
            //eslint-disable-next-line no-console
            console.log(`Measure ${config.name} is not found in the measures' list`);
            return undefined;
          }
          const copied: ReportField = {
            type: DimensionFieldType.MEASURE,
            config: {
              ...config,
              format:
                config.format ||
                (measure?.configuration?.format?.formatType === FormatType.Numeric
                  ? measure?.configuration?.format
                  : undefined),
              dateRange: config.dateRange || MeasureDateRange.EndingBalance,
            },
            meta: measure,
            areaItemType: AreaItemType.ROWS,
          };
          return copied;
        }
        const dimension = dimensions.find((d) => d.name === config.name);
        if (dimension) {
          const copied: DimensionField = {
            type: DimensionFieldType.DIMENSION,
            config,
            meta: dimension,
            areaItemType: AreaItemType.ROWS,
          };
          return copied;
        }
        //eslint-disable-next-line no-console
        console.log(`Field ${config.name} is not found in the dimensions' list`);
        return undefined;
      })
      .filter((c): c is DimensionField => !!c);
    return rows;
  } catch {
    return [];
  }
};

export const configurationToColumns = (
  configColumns: FieldConfiguration[],
  dimensions: DimensionDescriptor[],
  measures: MeasureDescriptor[]
) => {
  try {
    const columns = configColumns
      .map((config) => {
        if (config.type === FieldConfigurationType.Measure) {
          const measure = measures.find((d) => d.name === config.name);
          if (!measure || !measure.capabilities.isScalar) {
            //eslint-disable-next-line no-console
            console.log(`Measure ${config.name} is not found in the measures' list`);
            return undefined;
          }
          const copied: ReportField = {
            type: DimensionFieldType.MEASURE,
            config: {
              ...config,
              format:
                config.format ||
                (measure?.configuration?.format?.formatType === FormatType.Numeric
                  ? measure?.configuration?.format
                  : undefined),
              dateRange: config.dateRange || MeasureDateRange.EndingBalance,
            },
            meta: measure,
            areaItemType: AreaItemType.COLUMNS,
          };
          return copied;
        }
        const dimension = dimensions.find((d) => d.name === config.name);
        if (dimension) {
          const copied: DimensionField = {
            type: DimensionFieldType.DIMENSION,
            config,
            meta: dimension,
            areaItemType: AreaItemType.COLUMNS,
          };
          return copied;
        }
        //eslint-disable-next-line no-console
        console.log(`Field ${config.name} is not found in the dimensions' list`);
        return undefined;
      })
      .filter((c): c is DimensionField => !!c);
    return columns;
  } catch {
    return [];
  }
};

export const configurationToValues = (
  configValues: FieldConfiguration[],
  measures: MeasureDescriptor[],
  metrics: MetricDescriptor[],
  dimensions: DimensionDescriptor[],
  aggregations: AggregationDescriptor[] = []
) => {
  try {
    const values = configValues
      .map((config) => {
        if (config.type === FieldConfigurationType.Aggregation && config.persistedAggregationName) {
          const aggMeta = aggregations.find((a) => a.name === config.persistedAggregationName);
          if (aggMeta) {
            return <DimensionField>{
              type: DimensionFieldType.DIMENSION,
              meta: {
                ...aggMeta,
                itemType: MetaItemType.DIMENSION,
                keyFieldName: "",
                isNew: false,
                availableFields: [],
                displayValues: [],
                dictionary: [],
                glAllowed: false,
              },
              config,
              areaItemType: AreaItemType.VALUES,
            };
          }
        }
        const measure = measures.find((d) => d.name === config.name);
        if (measure) {
          const copied: ReportField = {
            type: DimensionFieldType.MEASURE,
            config: {
              ...config,
              format:
                config.format ||
                (measure?.configuration?.format?.formatType === FormatType.Numeric
                  ? measure?.configuration?.format
                  : undefined),
              dateRange: config.dateRange || MeasureDateRange.EndingBalance,
            },
            meta: measure,
            areaItemType: AreaItemType.VALUES,
          };
          return copied;
        }

        const metric = metrics.find((d) => d.name === config.name);
        if (metric) {
          const copied: MetricField = {
            type: DimensionFieldType.METRIC,
            config,
            meta: metric,
            areaItemType: AreaItemType.VALUES,
          };
          return copied;
        }

        if (config.type === FieldConfigurationType.Custom) {
          return <CustomColumnField>{
            type: DimensionFieldType.CUSTOM,
            meta: {
              caption: config.name,
              type: ItemDataType.Numeric,
              name: config.name,
              itemType: MetaItemType.CUSTOM,
              usage: MeasureUsage.General,
              group: "",
            },
            areaItemType: AreaItemType.VALUES,
            config,
          };
        }

        const dimension = dimensions.find((d) => d.name === config.name);
        if (dimension) {
          const copied: DimensionField = {
            type: DimensionFieldType.DIMENSION,
            config: {
              ...config,
              aggregation: config.aggregation || BiAggregation.Count,
              dateRange: config.dateRange || MeasureDateRange.EndingBalance,
            },
            meta: dimension,
            areaItemType: AreaItemType.VALUES,
          };
          return copied;
        }
        //eslint-disable-next-line no-console
        console.log(`Field ${config.name} is not found in the measures' list`);
        return undefined;
      })
      .filter((c) => c !== undefined);
    return values;
  } catch {
    return [];
  }
};
